import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, IconButton, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import { ArrowDownward, ArrowUpward, MenuBook } from '@mui/icons-material';
import { toast } from 'react-toastify';
import suggestionService, { BuildItem, SuggestedSkill, Suggestions } from '../../services/suggestion.service';
import courseService from '../../services/course.service';
import { Course } from '../../types';

/** Durées par défaut (minutes) : aucune donnée réelle n'existe encore, c'est une estimation modifiable par SF. */
const MIN_LIBRARY = 6;
const MIN_BAC = 12;
const MIN_COURSE = 10;
const DEFAULT_COUNT = 3;
const DEFAULT_KEPT = 8;
const MAX_COUNT = 6;

interface Row extends SuggestedSkill {
  chapitre: string;
  groupe: string;
  kept: boolean;
  count: number;
  source: 'library' | 'bac';
  course_on: boolean;
  minutes: number;
  minutes_edited: boolean;
}

const estimate = (r: Pick<Row, 'count' | 'source' | 'course_on'>) =>
  r.count * (r.source === 'bac' ? MIN_BAC : MIN_LIBRARY) + (r.course_on ? MIN_COURSE : 0);
const fmt = (min: number) => (min < 60 ? `${min} min` : `${Math.floor(min / 60)} h ${String(min % 60).padStart(2, '0')}`);

const toRows = (d: Suggestions): Row[] => {
  const all: Row[] = [];
  d.chapitres.forEach((c) => c.groupes.forEach((g) => g.skills.forEach((s) => all.push({
    ...s, chapitre: c.title, groupe: g.title, kept: false, count: DEFAULT_COUNT,
    source: s.library_count > 0 || s.bac_count === 0 ? 'library' : 'bac',
    course_on: false, minutes: 0, minutes_edited: false,
  }))));
  all.sort((a, b) => a.rang - b.rang);
  return all.map((r, i) => ({ ...r, kept: i < DEFAULT_KEPT, minutes: estimate(r) }));
};

interface Props {
  student: { id: number; first_name: string } | null;
  onSessionCreated: () => void;
}

/** Colonne « Suggestions » : bouquet de savoir-faire de l'élève (préconisations Bac/Brevet) → « Créer la séance ». */
export default function SuggestionsColumn({ student, onSessionCreated }: Props) {
  const [data, setData] = useState<Suggestions | null>(null);
  const [error, setError] = useState('');
  const [rows, setRows] = useState<Row[]>([]);
  const [chapters, setChapters] = useState<Record<string, boolean>>({});
  const [groups, setGroups] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);
  const [linking, setLinking] = useState<Row | null>(null);
  const [courses, setCourses] = useState<Course[]>([]);
  const [courseChoice, setCourseChoice] = useState<Course | null>(null);
  const studentId = student?.id;

  useEffect(() => {
    setData(null);
    setRows([]);
    setError('');
    if (!studentId) return undefined;
    let cancelled = false;
    suggestionService.get(studentId).then((d) => {
      if (cancelled) return;
      setData(d);
      setRows(toRows(d));
      const ch: Record<string, boolean> = {};
      const gr: Record<string, boolean> = {};
      d.chapitres.forEach((c) => {
        ch[c.title] = true;
        c.groupes.forEach((g) => { gr[`${c.title}|${g.title}`] = true; });
      });
      setChapters(ch);
      setGroups(gr);
    }).catch(() => { if (!cancelled) setError('Suggestions indisponibles pour cet élève'); });
    return () => { cancelled = true; };
  }, [studentId]);

  const visible = useMemo(
    () => rows.filter((r) => chapters[r.chapitre] && groups[`${r.chapitre}|${r.groupe}`]),
    [rows, chapters, groups],
  );
  const selected = useMemo(() => visible.filter((r) => r.kept), [visible]);
  const totalEx = selected.reduce((n, r) => n + r.count, 0);
  const totalMin = selected.reduce((n, r) => n + r.minutes, 0);

  const patch = (id: number, p: Partial<Row>) => setRows((rs) => rs.map((r) => {
    if (r.id !== id) return r;
    const next = { ...r, ...p };
    if (!next.minutes_edited && p.minutes === undefined) next.minutes = estimate(next);
    return next;
  }));
  // déplace la ligne au-delà de sa voisine *visible* (les lignes masquées par les filtres ne comptent pas)
  const move = (id: number, dir: -1 | 1) => setRows((rs) => {
    const vis = rs.filter((r) => chapters[r.chapitre] && groups[`${r.chapitre}|${r.groupe}`]);
    const vi = vis.findIndex((r) => r.id === id);
    const neighbour = vis[vi + dir];
    if (vi < 0 || !neighbour) return rs;
    const copy = rs.filter((r) => r.id !== id);
    const row = rs[rs.findIndex((r) => r.id === id)];
    const at = copy.findIndex((r) => r.id === neighbour.id) + (dir === 1 ? 1 : 0);
    copy.splice(at, 0, row);
    return copy;
  });
  const toggleChapter = (title: string, on: boolean) => {
    setChapters((c) => ({ ...c, [title]: on }));
    setGroups((g) => {
      const n = { ...g };
      Object.keys(n).filter((k) => k.startsWith(`${title}|`)).forEach((k) => { n[k] = on; });
      return n;
    });
  };

  const openLink = async (row: Row) => {
    setLinking(row);
    setCourseChoice(null);
    if (!courses.length) {
      try { setCourses(await courseService.getCourses()); } catch { toast.error('Cours indisponibles'); }
    }
  };
  const confirmLink = async () => {
    if (!linking || !courseChoice) return;
    try {
      await suggestionService.linkCourse(linking.id, courseChoice.id);
      patch(linking.id, { course: { id: courseChoice.id, title: courseChoice.title }, course_on: true });
      setLinking(null);
    } catch {
      toast.error('Impossible de lier ce cours');
    }
  };

  const create = useCallback(async () => {
    if (!studentId || !selected.length) return;
    setBusy(true);
    try {
      const items: BuildItem[] = selected.map((r) => ({
        skill: r.id, count: r.count, source: r.source, course: r.course_on && !!r.course, minutes: r.minutes,
      }));
      const res = await suggestionService.build(studentId, items);
      toast.success(`Séance S${res.session.number} créée · ${res.exercises} exercices`
        + (res.skipped.length ? ` · ${res.skipped.length} SF sans exercice` : ''));
      onSessionCreated();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Création impossible');
    } finally {
      setBusy(false);
    }
  }, [studentId, selected, onSessionCreated]);

  if (!student) {
    return <Box sx={{ p: 2 }}><Typography color="text.secondary">Sélectionne un élève</Typography></Box>;
  }
  if (error) return <Box sx={{ p: 2 }}><Alert severity="warning">{error}</Alert></Box>;
  if (!data) return <Box sx={{ p: 2, textAlign: 'center' }}><CircularProgress size={24} /></Box>;
  if (!data.chapitres.length) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="text.secondary">Aucune suggestion : tout est coché, ou pas de série (Terminale, Première, 3ème).</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Box sx={{ p: 1.5, borderBottom: '1px solid #e0e0e0', maxHeight: '40%', overflow: 'auto' }}>
        <Typography variant="subtitle2" fontWeight="bold">
          Suggestions pour {student.first_name} · {data.note.toFixed(2).replace('.', ',')} / 20
        </Typography>
        {data.chapitres.map((c) => (
          <Box key={c.title}>
            <FormControlLabel
              sx={{ mr: 0 }}
              control={<Checkbox size="small" checked={!!chapters[c.title]} onChange={(_, on) => toggleChapter(c.title, on)} />}
              label={<Typography variant="body2" fontWeight="bold">{c.title}</Typography>}
            />
            {chapters[c.title] && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', ml: 3 }}>
                {c.groupes.map((g) => {
                  const k = `${c.title}|${g.title}`;
                  return (
                    <FormControlLabel
                      key={k}
                      sx={{ mr: 1 }}
                      control={<Checkbox size="small" checked={!!groups[k]} onChange={(_, on) => setGroups((gr) => ({ ...gr, [k]: on }))} />}
                      label={<Typography variant="caption">{g.title}</Typography>}
                    />
                  );
                })}
              </Box>
            )}
          </Box>
        ))}
      </Box>
      <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
        {visible.map((r, idx) => (
          <Box
            key={r.id}
            sx={{
              display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 1, p: 0.5, borderRadius: 1,
              bgcolor: r.kept ? 'action.hover' : 'transparent', opacity: r.kept ? 1 : 0.6,
            }}
          >
            <Checkbox size="small" checked={r.kept} onChange={(_, on) => patch(r.id, { kept: on })} sx={{ p: 0.5 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ lineHeight: 1.2 }}>{r.name}</Typography>
              <Typography variant="caption" color="text.secondary">{r.chapitre} · {r.groupe}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                <Chip size="small" label={`+${r.centiemes} c`} />
                <Tooltip title="Nombre d'exercices">
                  <TextField
                    size="small"
                    type="number"
                    value={r.count}
                    inputProps={{ min: 1, max: MAX_COUNT, style: { width: 36, padding: 4 } }}
                    onChange={(e) => patch(r.id, { count: Math.max(1, Math.min(MAX_COUNT, Number(e.target.value) || 1)) })}
                  />
                </Tooltip>
                <ToggleButtonGroup size="small" exclusive value={r.source} onChange={(_, v) => v && patch(r.id, { source: v })}>
                  <ToggleButton value="library" disabled={r.library_count === 0} sx={{ py: 0, fontSize: 11 }}>Biblio</ToggleButton>
                  <ToggleButton value="bac" disabled={r.bac_count === 0} sx={{ py: 0, fontSize: 11 }}>Bac</ToggleButton>
                </ToggleButtonGroup>
                <Tooltip title={r.course ? `Cours : ${r.course.title}` : 'Lier un cours'}>
                  <span>
                    {r.course ? (
                      <Checkbox
                        size="small"
                        icon={<MenuBook fontSize="small" />}
                        checkedIcon={<MenuBook fontSize="small" color="primary" />}
                        checked={r.course_on}
                        onChange={(_, on) => patch(r.id, { course_on: on })}
                        sx={{ p: 0.5 }}
                      />
                    ) : (
                      <IconButton size="small" onClick={() => openLink(r)} sx={{ opacity: 0.5 }}>
                        <MenuBook fontSize="small" />
                      </IconButton>
                    )}
                  </span>
                </Tooltip>
                <Tooltip title="Durée estimée (minutes)">
                  <TextField
                    size="small"
                    type="number"
                    value={r.minutes}
                    inputProps={{ min: 0, style: { width: 44, padding: 4 } }}
                    InputProps={{ endAdornment: <Typography variant="caption">min</Typography> }}
                    onChange={(e) => patch(r.id, { minutes: Math.max(0, Number(e.target.value) || 0), minutes_edited: true })}
                  />
                </Tooltip>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <IconButton size="small" disabled={idx === 0} onClick={() => move(r.id, -1)}>
                <ArrowUpward sx={{ fontSize: 16 }} />
              </IconButton>
              <IconButton size="small" disabled={idx === visible.length - 1} onClick={() => move(r.id, 1)}>
                <ArrowDownward sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          </Box>
        ))}
      </Box>
      <Box sx={{ p: 1.5, borderTop: '1px solid #e0e0e0' }}>
        <Typography variant="caption" color="text.secondary" display="block">
          {selected.length} SF · {totalEx} exercices · ≈ {fmt(totalMin)} (estimation)
        </Typography>
        <Button variant="contained" fullWidth sx={{ mt: 1 }} disabled={busy || !selected.length} onClick={create}>
          {busy ? <CircularProgress size={20} /> : 'Créer la séance'}
        </Button>
      </Box>
      <Dialog open={!!linking} onClose={() => setLinking(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Cours pour « {linking?.name} »</DialogTitle>
        <DialogContent>
          <Autocomplete
            options={courses}
            getOptionLabel={(c) => c.title}
            value={courseChoice}
            onChange={(_, v) => setCourseChoice(v)}
            renderInput={(p) => <TextField {...p} autoFocus margin="dense" label="Cours" />}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinking(null)}>Annuler</Button>
          <Button variant="contained" disabled={!courseChoice} onClick={confirmLink}>Lier</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
